const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";
const TOKEN_KEY = "yma-admin-token";

interface RequestOptions extends RequestInit {
  auth?: boolean;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

function messageFromBody(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object") return fallback;
  const message = (body as { message?: string | string[] }).message;
  return Array.isArray(message) ? message.join("；") : message || fallback;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = new Headers(options.headers);
  if (options.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (options.auth !== false && token) headers.set("authorization", `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch {
    throw new ApiError("无法连接 YMA 服务端，请检查网络或服务状态", 0);
  }
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401 && options.auth !== false) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem("yma-admin-identity");
      window.dispatchEvent(new Event("yma:unauthorized"));
    }
    throw new ApiError(messageFromBody(body, `请求失败 (${response.status})`), response.status);
  }
  return body as T;
}

export async function uploadContentImage(file: File, scope: "service" | "company"): Promise<string> {
  const presign = await apiRequest<{ objectKey: string; uploadUrl: string }>("/admin/uploads/content/presign", {
    method: "POST",
    body: JSON.stringify({ fileName: file.name, mimeType: file.type, sizeBytes: file.size, scope })
  });
  const response = await fetch(presign.uploadUrl, { method: "PUT", headers: { "content-type": file.type }, body: file });
  if (!response.ok) throw new ApiError("图片上传失败，请重试", response.status);
  return presign.objectKey;
}

export { API_BASE, TOKEN_KEY };
